import { CircleCheck, CircleDashed, OctagonAlert, TriangleAlert, WifiOff, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import type { ChipTone, StatusTone } from '../../lib/metrics';

const ICONS: Record<ChipTone, LucideIcon> = {
  good: CircleCheck,
  warning: TriangleAlert,
  serious: WifiOff,
  critical: OctagonAlert,
  neutral: CircleDashed,
};

const ICON_COLOR: Record<ChipTone, string> = {
  good: 'text-good',
  warning: 'text-warning',
  serious: 'text-serious',
  critical: 'text-critical',
  neutral: 'text-neutral',
};

interface StatusIndicatorProps {
  tone: StatusTone;
  label: string;
  className?: string;
}

/** Icon + label, for dense rows. Colour never carries meaning alone. */
export function StatusIndicator({ tone, label, className }: StatusIndicatorProps) {
  const Icon = ICONS[tone];
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium text-fg', className)}>
      <Icon className={cn('flex-none', ICON_COLOR[tone])} size={14} strokeWidth={2.25} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

const CHIP: Record<ChipTone, string> = {
  good: 'bg-chip-good-bg text-chip-good-fg',
  warning: 'bg-chip-warning-bg text-chip-warning-fg',
  serious: 'bg-chip-serious-bg text-chip-serious-fg',
  critical: 'bg-chip-critical-bg text-chip-critical-fg',
  neutral: 'bg-chip-neutral-bg text-chip-neutral-fg',
};

interface StatusChipProps {
  tone: ChipTone;
  label: string;
  /** Leave the icon out where space is tight and the label says enough. */
  icon?: boolean;
  className?: string;
}

/** A tinted status pill: the main way state shows up on cards, rows and page headers. */
export function StatusChip({ tone, label, icon = true, className }: StatusChipProps) {
  const Icon = ICONS[tone];
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold',
        icon && 'pl-2',
        CHIP[tone],
        className,
      )}
    >
      {icon && <Icon className="flex-none" size={13} strokeWidth={2.4} aria-hidden />}
      {label}
    </span>
  );
}
